import { APP_ROUTES, isAppUnavailable, type AppKey } from './appRoutes';

export interface SearchPage {
  id: string;
  title: string;
  module: string;
  appId: AppKey;
  path: string;
  hint: string;
  keywords: string[];
  primary?: boolean;
  rank: number;
  requiresSettings?: boolean;
  disabled?: boolean;
}

export interface SearchAccess {
  apps: string[];
  isAdmin: boolean;
  canViewSettings: boolean;
}

const MODULE_KEYWORDS: Partial<Record<AppKey, string[]>> = {
  home: ['home', 'dashboard', 'overview'],
  reception: ['reception', 'front desk', 'frontdesk', 'hotel', 'check in', 'check-in'],
  pos: [
    'pos',
    'point of sale',
    'restaurant',
    'food',
    'sell',
    'sale',
    'sales',
    'dining',
    'menu',
    'kitchen',
    'waiter',
    'f&b',
    'beverage',
  ],
  accountant: ['accountant', 'accounts', 'finance', 'ledger'],
  inventory: ['inventory', 'stock', 'warehouse', 'supplies', 'store'],
  hr: ['hr', 'human resources', 'staff', 'employees', 'roster'],
  reports: ['reports', 'analytics', 'bi', 'business intelligence'],
  tourAgents: ['safari', 'tours', 'tour agents', 'travel'],
  africaVerify: ['africa verify', 'verification', 'kyc', 'documents'],
  odoCars: ['odo', 'cars', 'vehicles', 'fleet'],
  communicationHub: ['communication', 'messages', 'sms', 'whatsapp', 'email'],
  spa: ['spa', 'wellness', 'treatments'],
  marketing: ['guest sales', 'marketing', 'crm', 'inquiries'],
  groupEvents: ['group events', 'events', 'banquet', 'conference'],
};

interface PageDraft {
  appId: AppKey;
  path: string;
  title: string;
  hint: string;
  keywords?: string[];
  primary?: boolean;
  rank?: number;
  requiresSettings?: boolean;
  disabled?: boolean;
}

const PAGE_DRAFTS: PageDraft[] = [
  {
    appId: 'home',
    path: '/dashboard',
    title: 'Dashboard',
    hint: 'Applications',
    keywords: ['apps', 'applications', 'home'],
    primary: true,
    rank: 0,
  },
  {
    appId: 'home',
    path: '/profile',
    title: 'Profile',
    hint: 'Your account',
    keywords: ['account', 'me', 'profile'],
    rank: 1,
  },
  {
    appId: 'home',
    path: '/settings/users',
    title: 'Users',
    hint: 'Settings',
    keywords: ['users', 'staff accounts', 'settings'],
    requiresSettings: true,
    rank: 2,
  },
  {
    appId: 'home',
    path: '/settings/audit-logs',
    title: 'Audit logs',
    hint: 'Settings',
    keywords: ['audit', 'notifications', 'logs'],
    requiresSettings: true,
    rank: 3,
  },
  {
    appId: 'home',
    path: '/settings/system',
    title: 'System',
    hint: 'Settings',
    keywords: ['system', 'configuration', 'config'],
    requiresSettings: true,
    rank: 4,
  },
  {
    appId: 'home',
    path: '/settings/permissions',
    title: 'Permissions',
    hint: 'Settings',
    keywords: ['permissions', 'roles', 'access'],
    requiresSettings: true,
    rank: 5,
  },
  {
    appId: 'home',
    path: '/settings/email-templates',
    title: 'Email templates',
    hint: 'Settings',
    keywords: ['email', 'templates'],
    requiresSettings: true,
    rank: 6,
  },
  {
    appId: 'home',
    path: '/settings/backup',
    title: 'Backup',
    hint: 'Settings',
    keywords: ['backup', 'restore'],
    requiresSettings: true,
    rank: 7,
  },
  {
    appId: 'home',
    path: '/settings/currencies',
    title: 'Currencies',
    hint: 'Settings',
    keywords: ['currency', 'currencies', 'money'],
    requiresSettings: true,
    rank: 8,
  },

  {
    appId: 'reception',
    path: '/dashboard',
    title: 'Dashboard',
    hint: 'Overview',
    keywords: ['reception dashboard'],
    rank: 0,
  },
  {
    appId: 'reception',
    path: '/rooms',
    title: 'Rooms',
    hint: 'Room matrix',
    keywords: ['rooms', 'occupancy'],
    primary: true,
    rank: 1,
  },
  {
    appId: 'reception',
    path: '/bookings',
    title: 'Bookings',
    hint: 'Reservations',
    keywords: ['bookings', 'reservations', 'folio'],
    rank: 2,
  },
  {
    appId: 'reception',
    path: '/calendar',
    title: 'Calendar',
    hint: 'Schedule',
    keywords: ['calendar', 'arrivals'],
    rank: 3,
  },
  {
    appId: 'reception',
    path: '/guests',
    title: 'Guests',
    hint: 'Guest profiles',
    keywords: ['guests', 'customers'],
    rank: 4,
  },
  {
    appId: 'reception',
    path: '/room-types',
    title: 'Room types',
    hint: 'Categories',
    keywords: ['room types', 'categories'],
    rank: 5,
  },
  {
    appId: 'reception',
    path: '/seasonal-pricing',
    title: 'Seasonal pricing',
    hint: 'Rates',
    keywords: ['rates', 'pricing', 'seasonal'],
    rank: 6,
  },
  {
    appId: 'reception',
    path: '/maintenance',
    title: 'Maintenance',
    hint: 'Issues and cleaning',
    keywords: ['maintenance', 'housekeeping', 'cleaning'],
    rank: 7,
  },

  {
    appId: 'pos',
    path: '/products',
    title: 'Products',
    hint: 'Sell food and drinks',
    keywords: ['products', 'menu', 'sell', 'order taking'],
    primary: true,
    rank: 0,
  },
  {
    appId: 'pos',
    path: '/orders',
    title: 'Orders',
    hint: 'Active checks',
    keywords: ['orders', 'kitchen', 'checks'],
    rank: 1,
  },
  {
    appId: 'pos',
    path: '/tables',
    title: 'Tables',
    hint: 'Floor plan',
    keywords: ['tables', 'floor', 'dining room'],
    rank: 2,
  },
  {
    appId: 'pos',
    path: '/history',
    title: 'History',
    hint: 'Past transactions',
    keywords: ['history', 'past orders', 'receipts'],
    rank: 3,
  },
  {
    appId: 'pos',
    path: '/calendar',
    title: 'Calendar',
    hint: 'Daily overview',
    keywords: ['calendar'],
    rank: 4,
  },

  {
    appId: 'inventory',
    path: '/products',
    title: 'Inventory',
    hint: 'Products',
    keywords: ['products', 'catalogue', 'items'],
    primary: true,
    rank: 0,
  },
  {
    appId: 'inventory',
    path: '/meal-packages',
    title: 'Packages',
    hint: 'Product combos',
    keywords: ['packages', 'meal packages', 'combos'],
    rank: 1,
  },
  {
    appId: 'inventory',
    path: '/categories',
    title: 'Categories',
    hint: 'Product groups',
    keywords: ['categories'],
    rank: 2,
  },
  {
    appId: 'inventory',
    path: '/stock',
    title: 'Stock',
    hint: 'Stock levels',
    keywords: ['stock', 'on hand'],
    rank: 3,
  },
  {
    appId: 'inventory',
    path: '/suppliers',
    title: 'Suppliers',
    hint: 'Vendor directory',
    keywords: ['suppliers', 'vendors'],
    rank: 4,
  },
  {
    appId: 'inventory',
    path: '/purchase-orders',
    title: 'Purchase orders',
    hint: 'Procurement',
    keywords: ['purchase orders', 'po', 'procurement'],
    rank: 5,
  },
  {
    appId: 'inventory',
    path: '/assets',
    title: 'Asset tracking',
    hint: 'Fixed assets',
    keywords: ['assets', 'equipment'],
    rank: 6,
  },
  {
    appId: 'inventory',
    path: '/stock-counts',
    title: 'Stock counts',
    hint: 'Cycle counts',
    keywords: ['stock count', 'cycle count'],
    rank: 7,
  },

  {
    appId: 'accountant',
    path: '/overview',
    title: 'Overview',
    hint: 'Financial dashboard',
    keywords: ['overview'],
    primary: true,
    rank: 0,
  },
  {
    appId: 'accountant',
    path: '/transactions',
    title: 'Transactions',
    hint: 'Ledger entries',
    keywords: ['transactions', 'ledger'],
    rank: 1,
  },
  {
    appId: 'accountant',
    path: '/reconciliation',
    title: 'Reconciliation',
    hint: 'Collected by tender',
    keywords: ['reconciliation', 'tender'],
    rank: 2,
  },
  {
    appId: 'accountant',
    path: '/reports',
    title: 'Financial reports',
    hint: 'P&L and balance sheet',
    keywords: ['reports', 'profit', 'balance sheet'],
    rank: 3,
  },
  {
    appId: 'accountant',
    path: '/purchase-orders',
    title: 'Purchase orders',
    hint: 'Procurement',
    keywords: ['purchase orders', 'po'],
    rank: 4,
  },
  {
    appId: 'accountant',
    path: '/inventory-valuation',
    title: 'Valuation',
    hint: 'Stock worth',
    keywords: ['valuation', 'inventory valuation'],
    rank: 5,
  },

  {
    appId: 'hr',
    path: '/',
    title: 'Employees',
    hint: 'Staff directory',
    keywords: ['employees', 'staff'],
    primary: true,
    rank: 0,
  },
  {
    appId: 'hr',
    path: '/departments',
    title: 'Departments',
    hint: 'Headcount by team',
    keywords: ['departments', 'teams'],
    rank: 1,
  },
  {
    appId: 'hr',
    path: '/leave',
    title: 'Leave',
    hint: 'Time off',
    keywords: ['leave', 'time off', 'vacation'],
    rank: 2,
  },
  {
    appId: 'hr',
    path: '/shifts',
    title: 'Shifts',
    hint: 'Rosters',
    keywords: ['shifts', 'roster', 'schedule'],
    rank: 3,
  },
  {
    appId: 'hr',
    path: '/calendar',
    title: 'Shift board',
    hint: 'Weekly board and calendar',
    keywords: ['shift board', 'calendar', 'weekly shifts'],
    rank: 4,
  },
  {
    appId: 'hr',
    path: '/access',
    title: 'Access handoff',
    hint: 'Key and badge logs',
    keywords: ['access', 'keys', 'badges'],
    rank: 5,
  },

  {
    appId: 'reports',
    path: '/reports/general',
    title: 'General',
    hint: 'Overview',
    keywords: ['general'],
    primary: true,
    rank: 0,
  },
  {
    appId: 'reports',
    path: '/reports/reception',
    title: 'Reception report',
    hint: 'Front desk',
    keywords: ['reception'],
    rank: 1,
  },
  {
    appId: 'reports',
    path: '/reports/spa',
    title: 'Spa report',
    hint: 'Wellness',
    keywords: ['spa'],
    disabled: true,
    rank: 2,
  },
  {
    appId: 'reports',
    path: '/reports/sales',
    title: 'Sales report',
    hint: 'Revenue',
    keywords: ['sales'],
    disabled: true,
    rank: 3,
  },
  {
    appId: 'reports',
    path: '/reports/employees',
    title: 'Employees report',
    hint: 'Performance',
    keywords: ['employees', 'performance'],
    rank: 4,
  },
  {
    appId: 'reports',
    path: '/reports/bi',
    title: 'BI',
    hint: 'Analytics',
    keywords: ['bi', 'analytics'],
    rank: 5,
  },
  {
    appId: 'reports',
    path: '/reports/lifecycle',
    title: 'Lifecycle',
    hint: 'Kitchen',
    keywords: ['lifecycle', 'kitchen'],
    rank: 6,
  },
  {
    appId: 'reports',
    path: '/reports/food-timing',
    title: 'Food timing',
    hint: 'Peak hours',
    keywords: ['food timing', 'peak hours'],
    rank: 7,
  },
  {
    appId: 'reports',
    path: '/reports/downloads',
    title: 'Downloads',
    hint: 'Files',
    keywords: ['downloads', 'files'],
    rank: 8,
  },

  { appId: 'tourAgents', path: '/dashboard', title: 'Dashboard', hint: 'Overview', rank: 0 },
  {
    appId: 'tourAgents',
    path: '/bookings',
    title: 'Bookings',
    hint: 'Reservations',
    keywords: ['bookings'],
    primary: true,
    rank: 1,
  },
  {
    appId: 'tourAgents',
    path: '/clients',
    title: 'Clients',
    hint: 'Passports and contacts',
    keywords: ['clients'],
    rank: 2,
  },
  {
    appId: 'tourAgents',
    path: '/packages',
    title: 'Packages',
    hint: 'Itineraries',
    keywords: ['packages', 'itineraries'],
    rank: 3,
  },
  {
    appId: 'tourAgents',
    path: '/invoices',
    title: 'Invoices',
    hint: 'Billing',
    keywords: ['invoices'],
    rank: 4,
  },
  {
    appId: 'tourAgents',
    path: '/accounts',
    title: 'Accounts',
    hint: 'Payments',
    keywords: ['accounts'],
    rank: 5,
  },
  {
    appId: 'tourAgents',
    path: '/rates',
    title: 'Rate cards',
    hint: 'Hotel rates',
    keywords: ['rates', 'rate cards'],
    rank: 6,
  },
  {
    appId: 'tourAgents',
    path: '/providers',
    title: 'Providers',
    hint: 'Hotels and services',
    keywords: ['providers'],
    rank: 7,
  },
  {
    appId: 'tourAgents',
    path: '/calendar',
    title: 'Calendar',
    hint: 'Bookings timeline',
    keywords: ['calendar'],
    rank: 8,
  },
  {
    appId: 'tourAgents',
    path: '/map',
    title: 'Map',
    hint: 'Locations',
    keywords: ['map', 'locations'],
    rank: 9,
  },

  { appId: 'africaVerify', path: '/dashboard', title: 'Dashboard', hint: 'Overview', rank: 0 },
  {
    appId: 'africaVerify',
    path: '/verify',
    title: 'Documents',
    hint: 'Scan and validate',
    keywords: ['documents', 'verify', 'upload'],
    primary: true,
    rank: 1,
  },
  {
    appId: 'africaVerify',
    path: '/statements',
    title: 'Statements',
    hint: 'Bank statements',
    keywords: ['statements', 'bank'],
    rank: 2,
  },
  {
    appId: 'africaVerify',
    path: '/logbook',
    title: 'Logbook',
    hint: 'Vehicle records',
    keywords: ['logbook', 'vehicles'],
    rank: 3,
  },
  {
    appId: 'africaVerify',
    path: '/sessions',
    title: 'Sessions',
    hint: 'Verification history',
    keywords: ['sessions'],
    rank: 4,
  },
  {
    appId: 'africaVerify',
    path: '/connectors',
    title: 'Connectors',
    hint: 'Outbound endpoints',
    keywords: ['connectors'],
    rank: 5,
  },
  {
    appId: 'africaVerify',
    path: '/policies',
    title: 'Policies',
    hint: 'Terms',
    keywords: ['policies', 'terms'],
    rank: 6,
  },

  { appId: 'odoCars', path: '/dashboard', title: 'Dashboard', hint: 'Overview', rank: 0 },
  {
    appId: 'odoCars',
    path: '/inventory',
    title: 'Vehicle inventory',
    hint: 'Cars and listings',
    keywords: ['inventory', 'cars', 'listings'],
    primary: true,
    rank: 1,
  },
  {
    appId: 'odoCars',
    path: '/reservations',
    title: 'Reservations',
    hint: 'Customer inquiries',
    keywords: ['reservations'],
    rank: 2,
  },

  { appId: 'communicationHub', path: '/dashboard', title: 'Dashboard', hint: 'Overview', rank: 0 },
  {
    appId: 'communicationHub',
    path: '/email',
    title: 'Email',
    hint: 'Providers and templates',
    keywords: ['email'],
    primary: true,
    rank: 1,
  },
  {
    appId: 'communicationHub',
    path: '/mpesa',
    title: 'M-Pesa',
    hint: 'Daraja and STK push',
    keywords: ['mpesa', 'm-pesa', 'daraja', 'stk'],
    rank: 2,
  },
  {
    appId: 'communicationHub',
    path: '/api-keys',
    title: 'API keys',
    hint: 'Authentication',
    keywords: ['api keys', 'keys'],
    rank: 3,
  },
  {
    appId: 'communicationHub',
    path: '/logs',
    title: 'Logs',
    hint: 'Communication history',
    keywords: ['logs'],
    rank: 4,
  },
  {
    appId: 'communicationHub',
    path: '/docs',
    title: 'Docs',
    hint: 'API reference',
    keywords: ['docs', 'documentation'],
    rank: 5,
  },

  { appId: 'spa', path: '/', title: 'Dashboard', hint: 'Overview', rank: 0 },
  {
    appId: 'spa',
    path: '/treatments',
    title: 'Treatments',
    hint: 'Service menu',
    keywords: ['treatments'],
    primary: true,
    rank: 1,
  },
  {
    appId: 'spa',
    path: '/bookings',
    title: 'Bookings',
    hint: 'Appointments',
    keywords: ['bookings', 'appointments'],
    rank: 2,
  },
  {
    appId: 'spa',
    path: '/therapists',
    title: 'Therapists',
    hint: 'Staff profiles',
    keywords: ['therapists'],
    rank: 3,
  },
  {
    appId: 'spa',
    path: '/rooms',
    title: 'Rooms',
    hint: 'Treatment rooms',
    keywords: ['rooms'],
    rank: 4,
  },
  {
    appId: 'spa',
    path: '/schedule',
    title: 'Schedule',
    hint: 'Daily timetable',
    keywords: ['schedule'],
    rank: 5,
  },

  {
    appId: 'marketing',
    path: '/dashboard',
    title: 'Overview',
    hint: 'CRM dashboard',
    keywords: ['overview'],
    primary: true,
    rank: 0,
  },
  {
    appId: 'marketing',
    path: '/inquiries',
    title: 'Guest inquiries',
    hint: 'Lead pipeline',
    keywords: ['inquiries', 'leads'],
    rank: 1,
  },
  {
    appId: 'marketing',
    path: '/campaigns',
    title: 'Campaigns',
    hint: 'Active campaigns',
    keywords: ['campaigns'],
    rank: 2,
  },
  {
    appId: 'marketing',
    path: '/team',
    title: 'Guest liaisons',
    hint: 'Sales team',
    keywords: ['liaisons', 'sales team'],
    rank: 3,
  },

  { appId: 'groupEvents', path: '/', title: 'Dashboard', hint: 'Overview', rank: 0 },
  {
    appId: 'groupEvents',
    path: '/clients',
    title: 'Clients',
    hint: 'Corporate accounts',
    keywords: ['clients'],
    rank: 1,
  },
  {
    appId: 'groupEvents',
    path: '/events',
    title: 'Events',
    hint: 'Active bookings',
    keywords: ['events'],
    primary: true,
    rank: 2,
  },
  {
    appId: 'groupEvents',
    path: '/calendar',
    title: 'Calendar',
    hint: 'Event schedule',
    keywords: ['calendar'],
    rank: 3,
  },
  {
    appId: 'groupEvents',
    path: '/attendees',
    title: 'Attendees',
    hint: 'Guest lists',
    keywords: ['attendees'],
    rank: 4,
  },
  {
    appId: 'groupEvents',
    path: '/equipment',
    title: 'Equipment',
    hint: 'AV and rentals',
    keywords: ['equipment'],
    rank: 5,
  },
  {
    appId: 'groupEvents',
    path: '/spaces',
    title: 'Spaces',
    hint: 'Venues and halls',
    keywords: ['spaces', 'venues'],
    rank: 6,
  },
];

export const SEARCH_PAGES: SearchPage[] = PAGE_DRAFTS.map(page => ({
  id: `${page.appId}:${page.path}`,
  title: page.title,
  module: APP_ROUTES[page.appId].name,
  appId: page.appId,
  path: page.path,
  hint: page.hint,
  keywords: page.keywords || [],
  primary: page.primary,
  rank: page.rank ?? 0,
  requiresSettings: page.requiresSettings,
  disabled: page.disabled,
}));

export function searchPageHref(page: SearchPage): string {
  const base = APP_ROUTES[page.appId].baseUrl.replace(/\/$/, '');
  if (page.path === '/') return `${base}/`;
  return `${base}${page.path}`;
}

export function visibleSearchPages(access: SearchAccess): SearchPage[] {
  return SEARCH_PAGES.filter(page => {
    if (page.disabled || isAppUnavailable(page.appId)) return false;
    if (page.id === 'home:/profile') return true;
    if (page.requiresSettings) return access.isAdmin || access.canViewSettings;
    if (page.appId === 'home') return access.isAdmin || access.apps.includes('home');
    if (access.isAdmin) return true;
    return access.apps.includes(page.appId);
  });
}

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function hasTerm(value: string, query: string): boolean {
  return (
    value === query ||
    value.startsWith(`${query} `) ||
    value.endsWith(` ${query}`) ||
    value.includes(` ${query} `)
  );
}

function scorePage(page: SearchPage, query: string): number {
  const title = normalize(page.title);
  const moduleName = normalize(page.module);
  const hint = normalize(page.hint);
  const keywords = page.keywords.map(normalize);
  const moduleKeywords = (MODULE_KEYWORDS[page.appId] || []).map(normalize);
  const tokens = query.split(' ').filter(Boolean);
  let score = 0;

  if (title === query) score += 100;
  if (moduleName === query) score += page.primary ? 140 : 110;
  if (keywords.includes(query)) score += page.primary ? 96 : 80;
  if (moduleKeywords.includes(query)) score += page.primary ? 100 : 72;
  if (title.startsWith(`${query} `)) score += 36;
  if (keywords.some(keyword => hasTerm(keyword, query))) score += 28;
  if (moduleKeywords.some(keyword => hasTerm(keyword, query))) score += 18;
  if (hasTerm(hint, query)) score += 14;
  if (hasTerm(moduleName, query)) score += 12;

  const blob = [title, moduleName, hint, ...keywords, ...moduleKeywords].join(' ');
  if (tokens.length > 1) {
    const hits = tokens.filter(token => blob.includes(token)).length;
    if (hits === tokens.length) score += 40;
    else return 0;
  }

  return score;
}

export function searchPages(query: string, pages: SearchPage[], limit = 8): SearchPage[] {
  const normalized = normalize(query);
  if (!normalized) return [];

  return pages
    .map(page => ({ page, score: scorePage(page, normalized) }))
    .filter(row => row.score > 0)
    .sort(
      (left, right) =>
        right.score - left.score ||
        left.page.rank - right.page.rank ||
        left.page.module.localeCompare(right.page.module) ||
        left.page.title.localeCompare(right.page.title),
    )
    .slice(0, limit)
    .map(row => row.page);
}
